import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";
import { createZoomEvent } from "../../services/api";
import { inputStyle, labelStyle } from "./constants";

const DEFAULT_HOSTS = [
  { email: "tina@fordhamglobal.com", name: "Tina Fordham" },
  { email: "tim@fordhamglobal.com", name: "Tim Van Laere" },
];

export default function CreateEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState(() => {
    // Default to tomorrow at 14:00 local time
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(14, 0, 0, 0);
    const defaultDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return {
      title: "",
      description: "",
      scheduledAt: defaultDate,
      durationMinutes: 60,
      registerReminders: "7, 3",
      eventReminders: "1",
      panelistReminders: "7, 1",
    };
  });
  const [hosts, setHosts] = useState(DEFAULT_HOSTS.map((h) => h.email));
  const [customHost, setCustomHost] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!form.scheduledAt) {
      setError("Date and time are required");
      return;
    }

    const registerReminders = form.registerReminders
      .split(",")
      .map((s) => parseInt(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
    const eventReminders = form.eventReminders
      .split(",")
      .map((s) => parseInt(s.trim()))
      .filter((n) => !isNaN(n) && n >= 0);
    const panelistReminders = form.panelistReminders
      .split(",")
      .map((s) => parseInt(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);

    setSaving(true);
    try {
      const res = await createZoomEvent({
        title: form.title.trim(),
        description: form.description.trim(),
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        durationMinutes: Number(form.durationMinutes) || 60,
        hosts: hosts.filter((h) => h.trim()),
        reminderSettings: {
          registerReminders: registerReminders.length
            ? registerReminders
            : [7, 3],
          eventReminders: eventReminders.length ? eventReminders : [1],
          panelistReminders: panelistReminders.length
            ? panelistReminders
            : [7, 1],
        },
      });
      onCreated(res.event || res);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 100,
          pointerEvents: "none",
        }}
      >
        <form
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: 32,
            width: "100%",
            maxWidth: 520,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            pointerEvents: "auto",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 20,
                color: colors.text,
                margin: 0,
              }}
            >
              Create Webcast
            </h2>
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer" }}
              onClick={onClose}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Title <span style={{ color: colors.danger }}>*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={handleChange("title")}
              placeholder="Q1 Advisory Webcast"
              style={inputStyle}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={form.description}
              onChange={handleChange("description")}
              placeholder="Webcast description..."
              rows={3}
              style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>
                Date & Time <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={handleChange("scheduledAt")}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Duration (minutes)</label>
              <input
                type="number"
                value={form.durationMinutes}
                onChange={handleChange("durationMinutes")}
                min={15}
                max={480}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              borderTop: `1px solid ${colors.border}`,
              paddingTop: 16,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.text,
                marginBottom: 12,
              }}
            >
              Hosts
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 10 }}>
              Alternative hosts can start and manage the webinar. They receive a host invite with the start link.
            </div>
            {DEFAULT_HOSTS.map((h) => (
              <label
                key={h.email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 0",
                  fontSize: 13,
                  color: colors.text,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={hosts.includes(h.email)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setHosts((prev) => [...prev, h.email]);
                    } else {
                      setHosts((prev) => prev.filter((x) => x !== h.email));
                    }
                  }}
                />
                {h.name} <span style={{ color: colors.textSecondary, fontSize: 11 }}>({h.email})</span>
              </label>
            ))}
            {hosts
              .filter((h) => !DEFAULT_HOSTS.some((d) => d.email === h))
              .map((h) => (
                <div
                  key={h}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    fontSize: 13,
                    color: colors.text,
                  }}
                >
                  <input type="checkbox" checked readOnly />
                  {h}
                  <span
                    style={{ color: colors.danger, cursor: "pointer", fontSize: 11 }}
                    onClick={() => setHosts((prev) => prev.filter((x) => x !== h))}
                  >
                    remove
                  </span>
                </div>
              ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                type="email"
                value={customHost}
                onChange={(e) => setCustomHost(e.target.value)}
                placeholder="Add another host email..."
                style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const email = customHost.trim().toLowerCase();
                    if (email && !hosts.includes(email)) {
                      setHosts((prev) => [...prev, email]);
                      setCustomHost("");
                    }
                  }
                }}
              />
              <Button
                variant="default"
                type="button"
                style={{ fontSize: 12, padding: "6px 12px" }}
                onClick={() => {
                  const email = customHost.trim().toLowerCase();
                  if (email && !hosts.includes(email)) {
                    setHosts((prev) => [...prev, email]);
                    setCustomHost("");
                  }
                }}
              >
                Add
              </Button>
            </div>
          </div>

          <div
            style={{
              borderTop: `1px solid ${colors.border}`,
              paddingTop: 16,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.text,
                marginBottom: 12,
              }}
            >
              Reminder Settings
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <div>
                <label style={labelStyle}>
                  Register reminders (days before)
                </label>
                <input
                  type="text"
                  value={form.registerReminders}
                  onChange={handleChange("registerReminders")}
                  placeholder="7, 3"
                  style={inputStyle}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    marginTop: 3,
                  }}
                >
                  Comma-separated days for unregistered invitees
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Webcast reminders (days before)
                </label>
                <input
                  type="text"
                  value={form.eventReminders}
                  onChange={handleChange("eventReminders")}
                  placeholder="1"
                  style={inputStyle}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    marginTop: 3,
                  }}
                >
                  Comma-separated days for registered attendees
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Panelist reminders (days before)
                </label>
                <input
                  type="text"
                  value={form.panelistReminders}
                  onChange={handleChange("panelistReminders")}
                  placeholder="7, 1"
                  style={inputStyle}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    marginTop: 3,
                  }}
                >
                  Comma-separated days for panelists (includes prep checklist)
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "10px 14px",
                marginBottom: 16,
                borderRadius: 6,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: colors.danger,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="default" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" style={{ minWidth: 100 }}>
              {saving ? (
                <>
                  <Loader2
                    size={14}
                    className="spin"
                  />{" "}
                  Creating...
                </>
              ) : (
                "Create Webcast"
              )}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
